import { useState } from 'react';
import { ETemplateType, type TVisibility } from 'types/template/template.types';
import { chosenAccessGroupsAllowTemplateCreate } from 'utils/templateAccessGroups';

import type { SourceScopeItem, TCreateTemplateForm, TSourceScope } from '../types';

type Params = {
  isHubertAdmin: boolean;
  userClientId: string;
  canShareWithOrg: boolean;
  templateCreateAccessGroupIds?: string[];
  initialTemplate?: Partial<TCreateTemplateForm>;
  includeLanguageValidation?: boolean;
};

export type CreateTemplateFormState = {
  newTemplate: TCreateTemplateForm;
  isGlobalAllowed: boolean;
  isCopyMode: boolean;
  sourceScopeItems: SourceScopeItem[];
  canUseSourceClientForDestination: boolean;
  requiresDestinationClient: boolean;
  shouldShowUseSourceClientToggle: boolean;
  isDestinationDisabled: boolean;
  isDestinationRequired: boolean;
  isSubmitDisabled: boolean;
  chosenAccessGroupsLackTemplateCreate: boolean;
  canShareWithOrg: boolean;
};

export type CreateTemplateFormActions = {
  onNameChange: (value: string) => void;
  onTypeChange: (value: ETemplateType) => void;
  onModeChange: (mode: 'create' | 'copy') => void;
  onSourceScopeChange: (value: TSourceScope) => void;
  onSourceClientChange: (value: string) => void;
  onBaseTemplateChange: (value: string) => void;
  onDestinationChange: (value: string) => void;
  onUseSourceClientForDestinationChange: (value: boolean) => void;
  onVisibilityChange: (value: TVisibility) => void;
  onAccessGroupChange: (accessGroupIds: string[]) => void;
  onSupportedLanguagesChange: (value: string | string[]) => void;
  onAllowedLanguagesChange: (value: { code: string }[]) => void;
  onRoleNameChange: (value: string) => void;
  onCompanyNameChange: (value: string) => void;
};

export type UseCreateTemplateFormResult = CreateTemplateFormState & CreateTemplateFormActions;

export const useCreateTemplateForm = ({
  isHubertAdmin,
  userClientId,
  canShareWithOrg,
  templateCreateAccessGroupIds,
  initialTemplate,
  includeLanguageValidation,
}: Params): UseCreateTemplateFormResult => {
  const defaultTemplate: TCreateTemplateForm = {
    mode: 'copy',
    name: '',
    type: ETemplateType.Screening,
    visibility: canShareWithOrg ? 'client' : 'local',
    useSourceClientForDestination: false,
    sourceClientId: isHubertAdmin ? '' : userClientId,
    sourceScope: isHubertAdmin ? 'other' : 'this',
    destinationClientId: isHubertAdmin ? '' : userClientId,
  };

  const [newTemplate, setNewTemplate] = useState<TCreateTemplateForm>(() => {
    const merged = { ...defaultTemplate, ...(initialTemplate ?? {}) };
    if (merged.supportedLanguages?.length) {
      merged.allowedLanguages = [...new Set(merged.supportedLanguages.map((code) => code.split('-')[0]))].map(
        (code) => ({ code }),
      );
    }
    return merged;
  });

  const visibility = newTemplate.visibility;
  const isGlobalAllowed = isHubertAdmin && newTemplate.mode === 'create';
  const isCopyMode = newTemplate.mode === 'copy';

  const canUseSourceClientForDestination =
    isCopyMode && newTemplate.sourceScope === 'other' && Boolean(newTemplate.sourceClientId);
  const requiresDestinationClient = isHubertAdmin && visibility !== 'global';
  const shouldShowUseSourceClientToggle = isHubertAdmin && isCopyMode && newTemplate.sourceScope === 'other';
  const isDestinationDisabled = visibility === 'global';
  const isDestinationRequired = !isDestinationDisabled;

  const sourceScopeItems: SourceScopeItem[] = isHubertAdmin
    ? [
        { label: 'All clients', value: 'other' },
        { label: 'Global', value: 'global' },
      ]
    : [
        { label: 'Your assigned client', value: 'this' },
        { label: 'Global', value: 'global' },
      ];

  const languagesRequired = includeLanguageValidation
    ? !newTemplate.supportedLanguages?.length || !newTemplate.allowedLanguages?.length
    : false;

  const chosenAccessGroupIds = newTemplate.accessGroupIds ?? [];
  const chosenAccessGroupsLackTemplateCreate =
    visibility === 'local' &&
    chosenAccessGroupIds.length > 0 &&
    !chosenAccessGroupsAllowTemplateCreate(chosenAccessGroupIds, templateCreateAccessGroupIds);

  const isSubmitDisabled =
    !newTemplate.name.trim().length ||
    (requiresDestinationClient && !newTemplate.destinationClientId) ||
    (isCopyMode && !newTemplate.baseTemplateId) ||
    (isCopyMode && newTemplate.sourceScope === 'other' && !newTemplate.sourceClientId) ||
    (newTemplate.visibility === 'local' && !newTemplate.accessGroupIds?.length) ||
    chosenAccessGroupsLackTemplateCreate ||
    languagesRequired;

  const handleModeChange = (mode: 'create' | 'copy') => {
    setNewTemplate((prev) => {
      return {
        ...prev,
        mode,
        visibility: canShareWithOrg ? 'client' : 'local',
        useSourceClientForDestination: false,
        sourceClientId: isHubertAdmin ? '' : userClientId,
        sourceScope: isHubertAdmin ? 'other' : 'this',
        destinationClientId: isHubertAdmin ? '' : userClientId,
        baseTemplateId: mode === 'create' ? undefined : prev.baseTemplateId,
        accessGroupIds: [],
        startingLanguage: mode === 'create' ? undefined : prev.startingLanguage,
      };
    });
  };

  const handleDestinationChange = (value: string) => {
    setNewTemplate((prev) => {
      const shouldSyncSource = prev.mode === 'create' || prev.sourceScope === 'this';
      return {
        ...prev,
        destinationClientId: value,
        sourceClientId: shouldSyncSource ? value : prev.sourceClientId,
        visibility: value && prev.visibility === 'global' ? 'client' : prev.visibility,
        accessGroupIds: [],
      };
    });
  };

  const handleSourceScopeChange = (value: TSourceScope) => {
    setNewTemplate((prev) => {
      if (isHubertAdmin) {
        return {
          ...prev,
          sourceScope: value,
          useSourceClientForDestination: false,
          sourceClientId: '',
          destinationClientId: '',
          baseTemplateId: undefined,
        };
      }

      return {
        ...prev,
        sourceScope: value,
        sourceClientId: value === 'global' ? '' : prev.sourceClientId,
        baseTemplateId: undefined,
      };
    });
  };

  const handleSourceClientChange = (value: string) => {
    setNewTemplate((prev) => {
      const shouldUseSourceForDestination = value ? prev.useSourceClientForDestination : false;
      return {
        ...prev,
        sourceClientId: value,
        useSourceClientForDestination: shouldUseSourceForDestination,
        destinationClientId: shouldUseSourceForDestination ? value : prev.destinationClientId,
        accessGroupIds: shouldUseSourceForDestination ? [] : prev.accessGroupIds,
      };
    });
  };

  const handleVisibilityChange = (value: TVisibility) => {
    setNewTemplate((prev) => {
      const shouldClearAccessGroups = value !== 'local' && Boolean(prev.accessGroupIds?.length);
      const shouldClearDestination = value === 'global';

      if (!shouldClearAccessGroups && !shouldClearDestination) {
        return {
          ...prev,
          visibility: value,
        };
      }
      return {
        ...prev,
        visibility: value,
        useSourceClientForDestination: value === 'global' ? false : prev.useSourceClientForDestination,
        accessGroupIds: shouldClearAccessGroups ? [] : prev.accessGroupIds,
        destinationClientId: shouldClearDestination ? '' : prev.destinationClientId,
        sourceClientId: shouldClearDestination ? '' : prev.sourceClientId,
      };
    });
  };

  const handleUseSourceClientForDestinationChange = (value: boolean) => {
    setNewTemplate((prev) => ({
      ...prev,
      useSourceClientForDestination: value,
      destinationClientId: value && newTemplate.sourceClientId ? newTemplate.sourceClientId : prev.destinationClientId,
      accessGroupIds: value && newTemplate.sourceClientId ? [] : prev.accessGroupIds,
    }));
  };

  return {
    newTemplate,
    isGlobalAllowed,
    canShareWithOrg,
    isCopyMode,
    sourceScopeItems,
    canUseSourceClientForDestination,
    requiresDestinationClient,
    shouldShowUseSourceClientToggle,
    isDestinationDisabled,
    isDestinationRequired,
    isSubmitDisabled,
    chosenAccessGroupsLackTemplateCreate,
    onNameChange: (value: string) => setNewTemplate((prev) => ({ ...prev, name: value })),
    onTypeChange: (value: ETemplateType) => setNewTemplate((prev) => ({ ...prev, type: value })),
    onModeChange: handleModeChange,
    onSourceScopeChange: handleSourceScopeChange,
    onSourceClientChange: handleSourceClientChange,
    onBaseTemplateChange: (value: string) => setNewTemplate((prev) => ({ ...prev, baseTemplateId: value })),
    onDestinationChange: handleDestinationChange,
    onUseSourceClientForDestinationChange: handleUseSourceClientForDestinationChange,
    onVisibilityChange: handleVisibilityChange,
    onAccessGroupChange: (accessGroupIds: string[]) => setNewTemplate((prev) => ({ ...prev, accessGroupIds })),
    onSupportedLanguagesChange: (value: string | string[]) =>
      setNewTemplate((prev) => {
        const languages = Array.isArray(value) ? value : [value];
        return {
          ...prev,
          supportedLanguages: languages,
          startingLanguage: languages[0],
        };
      }),
    onAllowedLanguagesChange: (value: { code: string }[]) =>
      setNewTemplate((prev) => ({ ...prev, allowedLanguages: value })),
    onRoleNameChange: (value: string) => setNewTemplate((prev) => ({ ...prev, roleName: value })),
    onCompanyNameChange: (value: string) => setNewTemplate((prev) => ({ ...prev, companyName: value })),
  };
};
